import { ContentHeader, InfoCard, Link, Progress, SupportButton } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { docFeedbackApiRef } from '@internal/plugin-doc-feedback';
import {
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Theme,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { DEFAULT_TOP_VISITS_WINDOW_DAYS, fetchTopViewedDocs } from '../home/homeVisits';
import { TopDocLink, toTopDocLinks } from './topDocs';

const TOP_DOCS = 5;
/** Extra refs fetched so the list can still show 5 docs when some were removed from the catalog. */
const TOP_DOCS_FETCH = 10;

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  item: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  rank: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.type === 'dark' ? 0.24 : 0.1),
  },
  avatar: {
    minWidth: 44,
  },
  title: {
    fontWeight: 600,
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
}));

interface TopDocsCardProps {
  title: string;
  subheader: string;
  emptyText: string;
  /** Ranked doc entity refs, most popular first. */
  loadRefs: () => Promise<string[]>;
}

/** A ranked list of docs. Fails soft: errors show the empty text. */
const TopDocsCard = ({ title, subheader, emptyText, loadRefs }: TopDocsCardProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [docs, setDocs] = useState<TopDocLink[] | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let refs: string[] = [];
      try {
        refs = await loadRefs();
      } catch {
        refs = [];
      }
      let links: TopDocLink[];
      try {
        if (refs.length === 0) {
          throw new Error('Nothing to look up');
        }
        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: refs,
          fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.title', 'metadata.description'],
        });
        links = toTopDocLinks(refs, TOP_DOCS, items);
      } catch {
        links = toTopDocLinks(refs, TOP_DOCS);
      }
      if (!cancelled) setDocs(links);
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, loadRefs]);

  let content: ReactNode;
  if (!docs) {
    content = <Progress />;
  } else if (docs.length === 0) {
    content = (
      <Typography variant="body2" color="textSecondary">
        {emptyText}
      </Typography>
    );
  } else {
    content = (
      <List dense className={classes.list}>
        {docs.map((doc, index) => (
          <ListItem key={doc.entityRef} className={classes.item}>
            <ListItemAvatar className={classes.avatar}>
              <span className={classes.rank} aria-label={`Rank ${index + 1}`}>
                {index + 1}
              </span>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Link to={doc.path} className={classes.title}>
                  {doc.title}
                </Link>
              }
              secondary={doc.description}
              secondaryTypographyProps={{ className: classes.description }}
            />
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <InfoCard title={title} subheader={subheader} variant="fullHeight">
      {content}
    </InfoCard>
  );
};

/** "Top 5 most viewed docs" (all users, rolling window) and "Top 5 most liked docs" (👍 ratings). */
export const TopDocsSection = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const docFeedbackApi = useApi(docFeedbackApiRef);
  const [windowDays, setWindowDays] = useState(DEFAULT_TOP_VISITS_WINDOW_DAYS);

  const loadViewed = useCallback(async () => {
    const result = await fetchTopViewedDocs(discoveryApi, fetchApi, TOP_DOCS_FETCH);
    setWindowDays(result.windowDays);
    return result.entityRefs;
  }, [discoveryApi, fetchApi]);
  const loadLiked = useCallback(async () => {
    const result = await docFeedbackApi.getTopLikedDocs(TOP_DOCS_FETCH);
    return result.items.map(item => item.entityRef);
  }, [docFeedbackApi]);

  return (
    <Grid container spacing={3} className={classes.section} component="section" aria-label="Top docs">
      <Grid item xs={12} md={6}>
        <TopDocsCard
          title="Top 5 most viewed docs"
          subheader={`Across DevHub · last ${windowDays} ${windowDays === 1 ? 'day' : 'days'}`}
          emptyText="No docs have been viewed yet."
          loadRefs={loadViewed}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TopDocsCard
          title="Top 5 most liked docs"
          subheader="Most 👍 from readers"
          emptyText="No docs have been rated yet."
          loadRefs={loadLiked}
        />
      </Grid>
    </Grid>
  );
};

/** Header of the /docs index page: the support button and the Top docs section. */
export const DocsIndexHeader = () => (
  <>
    <ContentHeader title="">
      <SupportButton>Discover documentation in your ecosystem.</SupportButton>
    </ContentHeader>
    <TopDocsSection />
  </>
);
